import { getApiBaseUrl } from "@/config";
import { getOidcUser, refreshToken } from "@/user/userManager";

interface RawApiError {
    message: string;
}

export class ApiError extends Error {
    public status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }

    public isInvalidOrExpiredToken(): boolean {
        return this.status === 401;
    }

    public isPermissionDenied(): boolean {
        return this.status === 403;
    }
}

export class NetworkError extends Error {}

export async function apiCall<T>(
    endpoint: string,
    method: string,
    body?: unknown
): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const stringifiedBody = body ? JSON.stringify(body) : undefined;

    // if token is expired, refresh it before making api call
    const user = getOidcUser();
    if (user?.expires_at && user.expires_at < Date.now() / 1000) {
        await refreshToken();
    }

    let response: Response | undefined;
    try {
        response = await apiFetch(url, method, stringifiedBody);
    } catch (e) {
        throw new NetworkError(
            e instanceof Error ? e.message : "Unknown network error"
        );
    }

    if (!response.ok) {
        const rawError = (await response.json()) as RawApiError;
        const apiError = new ApiError(rawError.message, response.status);
        if (apiError.isInvalidOrExpiredToken()) {
            // refresh token
            await refreshToken();
            // retry the original call
            try {
                response = await apiFetch(url, method, stringifiedBody);
            } catch (e) {
                throw new NetworkError(
                    e instanceof Error ? e.message : "Unknown network error"
                );
            }
        } else {
            throw apiError;
        }
    }

    if (!response.ok) {
        const rawError = (await response.json()) as RawApiError;
        throw new ApiError(rawError.message, response.status);
    }

    if (response.status === 204) {
        return null as unknown as T; // currently assuming caller can handle 204 properly with null return
    }

    try {
        const json = (await response.json()) as T;
        return json;
    } catch (e) {
        throw new ApiError("Invalid JSON", response.status);
    }
}

function apiFetch(
    url: string,
    method: string,
    stringifiedBody: string | undefined
) {
    const headers: HeadersInit = {
        "Content-Type": "application/json"
    };

    const token = getOidcUser()?.id_token;
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(url, {
        method,
        headers,
        body: stringifiedBody
    });
}
